import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import { ClipboardList, MoreVertical, Megaphone, Users } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ChannelHeader } from '../../services/api';
import { useStore } from '../../store';
import { useIsMobile } from '../../hooks/useIsMobile';

interface ChannelMenuProps {
  header: ChannelHeader;
  onShowMembers: () => void;
  onShowAnnouncers: () => void;
}

/**
 * No archive item: archive is a Group Manager action (EMR-1880). "Manage group"
 * opens that group in Group Manager; the server reports `can_manage_group` false
 * for automatic and private channels, so no extra condition is needed here.
 */
export function ChannelMenu({
  header,
  onShowMembers,
  onShowAnnouncers,
}: ChannelMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const closePanel = useStore(state => state.closeChat);
  return (
    // Non-modal so the menu never writes `pointer-events: none` to the body for
    // the Group Manager drawer's separate Radix copy to re-apply on close.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button size='icon' variant='ghost' aria-label='Conversation menu'>
          <MoreVertical className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {header.patient_id && (
          <DropdownMenuItem
            onSelect={() => {
              navigate(
                `/patients/${header.patient_id}?episode=${header.episode_id}`
              );
              // Below md the panel covers the chart, so the chart would be
              // invisible after the navigation.
              if (isMobile) closePanel();
            }}
          >
            <ClipboardList className='mr-2 h-4 w-4' />
            Open chart
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onShowMembers}>
          <Users className='mr-2 h-4 w-4' />
          Members ({header.member_count})
        </DropdownMenuItem>
        {header.can_edit_announcers && (
          <DropdownMenuItem onClick={onShowAnnouncers}>
            <Megaphone className='mr-2 h-4 w-4' />
            Announcers
          </DropdownMenuItem>
        )}
        {header.can_manage_group && (
          <DropdownMenuItem
            onSelect={() => {
              // Record where we came from so closing the group drawer can
              // return here instead of stranding the person on the list.
              navigate(`/group-manager?group=${header.group_id}`, {
                state: { from: location.pathname + location.search },
              });
              // Below md the panel covers Group Manager, as it does the chart.
              if (isMobile) closePanel();
            }}
          >
            <Users className='mr-2 h-4 w-4' />
            Manage group
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
